import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { AlertController, NavController, Platform } from '@ionic/angular';
import {
  BehaviorSubject,
  catchError,
  filter,
  firstValueFrom,
  from,
  map,
  Observable,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { environment } from 'src/environments/environment';
import { APP_CONFIG } from '../config/app.config';
import { StorageService } from './storage.service';
import { ApiService } from './api.service';
import { User1Service } from './user1.service';
import { Role, Status } from '../shared/role';
import { Permission } from '../shared/permission';
import { TranslateConfigService } from './translate-config.service';
import { UserModel } from '../shared/models/user.model';
import { SignalRService } from './signalR.service';
import { PERMISSION_ROUTES } from '../shared/permission-routes';
import { Capacitor } from '@capacitor/core';
import { BgLocationPermission } from 'background-location-permission-plugin/src';

const USER_KEY = 'currentUser';
const TENANT_KEY = 'currentTenant';
const LANGUAGE_KEY = 'language';
const USER_REMEMBER = 'userRemember';
const PIN_RETURN_URL_KEY = 'returnUrlForPIN';
const FCM_KEY = 'FCM';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public currentUserSubject!: BehaviorSubject<UserModel>;
  private currentTenantSubject!: BehaviorSubject<any>;
  private currentFcmToken!: string;
  subscribeUser: any;
  subscribeTenant: any;

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private navController: NavController,
    private userService: User1Service,
    public alertCtrl: AlertController,
    private platform: Platform,
    private router: Router,
    private signalRService: SignalRService,
    private translateConfigService: TranslateConfigService,
    public storageService: StorageService
  ) {
    this.initializeIssueLogToken();
    this.storageService.get(USER_KEY).then((user) => {
      //console.log(user);
      const data = storageService.decryptData(user);
      if (data != null) {
        this.currentUserSubject = new BehaviorSubject<UserModel>(
          JSON.parse(data)
        );
      } else {
        this.currentUserSubject = new BehaviorSubject<UserModel>(
          new UserModel()
        );
      }
      this.subscribeUser = this.currentUserSubject.asObservable();
      this.currentTenantSubject = new BehaviorSubject({});
    });

    this.storageService.get(TENANT_KEY).then((tenant) => {
      this.subscribeTenant = this.currentUserSubject?.asObservable();
      this.currentTenantSubject = new BehaviorSubject(
        JSON.parse(tenant) ?? null
      );
    });
  }

  public get currentTenantValue(): any {
    if (this.currentTenantSubject) {
      return this.currentTenantSubject.getValue();
    }
    return new UserModel();
  }

  public get getCurrentFcmToken(): any {
    return this.currentFcmToken;
  }

  // async getCurrentUserValue() {
  //   this.storageService.get(USER_KEY).then((user) => {
  //     //console.log(user);
  //     const data = this.storageService.decryptData(user);
  //     if (data != null) {
  //       this.currentUserSubject = new BehaviorSubject<UserModel>(JSON.parse(data));
  //     } else {
  //       this.currentUserSubject = new BehaviorSubject<UserModel>(new UserModel());
  //     }
  //     this.subscribeUser = this.currentUserSubject.asObservable();
  //     this.currentTenantSubject = new BehaviorSubject({})
  //   });
  // }
  async getCurrentUserValue(): Promise<UserModel> {
    const user = await this.storageService.get(USER_KEY); // Await the result of get()

    if (user == null) return new UserModel();

    const data = this.storageService.decryptData(user);
    if (data == null) return new UserModel();

    this.currentUserSubject = new BehaviorSubject<UserModel>(JSON.parse(data));
    return JSON.parse(data) as UserModel;
  }

  public get currentUserValue(): UserModel {
    // console.log(this.currentUserSubject);

    //console.log(this.currentUserSubject);

    if (this.currentUserSubject) {
      return this.currentUserSubject.getValue();
    }

    return new UserModel();
  }

  setTenant(tenant: any) {
    this.currentTenantSubject.next(tenant);
    this.storageService.set(TENANT_KEY, JSON.stringify(tenant));
  }

  login(model: any) {
    return this.apiService.post(`/user/login`, model);
  }

  getFcmToken() {
    return this.apiService.get(`/user/fcm-token`);
  }

  redirectToNotFound() {
    this.router.navigateByUrl('not-found');
  }

  logout() {

    Promise.all([
      this.storageService.get(LANGUAGE_KEY),
      // this.storageService.get("userRemember")
      this.signalRService.stopConnection()
    ]).then((res: any) => {
      if (res[0]) this.storageService.set('LANGUAGE_KEY', res[0]);
      // if (res[1]) this.storageService.set('userRemember', res[1])
    });
    if (['android', 'ios'].includes(Capacitor.getPlatform())) {
      BgLocationPermission.hideDutyNotification().catch(() => {});
    }
    // remove user from local storage to log user out
    this.currentUserSubject.next(new UserModel());
    this.storageService.remove(USER_KEY);
    this.navController.navigateRoot(['/login']);
  }

  set(token: any) {
    if (token && token.access_token) {
      // console.log(token);
      this.currentUserSubject.next(token);
      const data = this.storageService.encryptData(JSON.stringify(token));
      // store user details and jwt token in local storage to keep user logged in between page refreshes
      this.storageService.set(USER_KEY, data);

      ///console.log(this.currentTenantSubject);
    }
  }

  public get fcmToken(): string {
    return this.currentFcmToken;
  }

  setFcmToken(token: string) {
    this.currentFcmToken = token;
  }

  clear() {
    // remove user from local storage to log user out
    this.storageService.get(LANGUAGE_KEY).then((res) => {
      // Remove all keys
      this.storageService.clear();
      // Keep saved language
      if (res) this.storageService.set(LANGUAGE_KEY, res);
    });

    this.currentUserSubject.next(new UserModel());
  }

  underMaintenance() {
    this.navController.navigateRoot(['/maintenance']);
  }

  pageNotFound() {
    this.navController.navigateRoot(['/not-found']);
  }

  setPin() {
    this.storageService.get(PIN_RETURN_URL_KEY).then((retUrl) => {
      let pinRedirectUrl = '/setting/pin';
      if (retUrl) {
        const tree: UrlTree = this.router.parseUrl(
          `${pinRedirectUrl}?returnUrl=${encodeURIComponent(retUrl)}`
        );
        this.navController.navigateRoot(tree);
        return;
      }
      this.navController.navigateRoot([pinRedirectUrl]);
    });
  }

  changePasswordFirstTime() {
    let pinRedirectUrl = '/change-password';
    this.navController.navigateRoot([pinRedirectUrl]);
  }

  eKycRequired() {
    this.navController.navigateRoot(['/profile']);
  }

  async upgradeRequired() {
    let isBrowser = false;
    if (this.platform.is('desktop') || this.platform.is('mobileweb')) {
      isBrowser = true;
    }
    const alert = await this.alertCtrl.create({
      header: this.translateConfigService.getTranslation('Upgrade required!'),
      // tslint:disable-next-line:max-line-length
      message: this.translateConfigService.getTranslation(
        isBrowser
          ? 'Oops. Looks like your browser is having some issues. Please press Ctrl+F5 or Command+R on MacOS to refresh your browser'
          : 'Please update to the newer version of the App by visiting your respective App Store.'
      ),
      subHeader: '',
      cssClass: 'info-dialog',
      buttons: [
        {
          text: 'Ok',
          handler: () => {
            if (isBrowser) {
              // Update the code clear cache here
              if ('serviceWorker' in navigator) {
                navigator.serviceWorker
                  .getRegistrations()
                  .then(function (registrations) {
                    //returns installed service workers
                    if (registrations.length) {
                      for (let registration of registrations) {
                        registration.unregister();
                      }
                    }
                    window.location.reload();
                  });
              }
            } else {
            }
          },
        },
      ],
    });
    await alert.present();
  }

  signin(
    model: { username: string; password: string },
    clientId: string,
    secretKey: string
  ): Observable<any> {
    const credentials = `username=${model.username}&password=${model.password}`;
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded')
      .set('x-api-key', 'bWlsbGlvbmFpcmVhc2lh')
      .set('clientId', clientId)
      .set('secretKey', secretKey)
      .set('Access-Control-Allow-Origin', '*')
      .set(
        'Access-Control-Allow-Headers',
        'Authorization,DNT,X-Mx-ReqToken,Keep-Alive,User-Agent,X-Requested-With,If-Modified-Since,Cache-Control,Content Type,Orgin'
      );
    return this.http.post(
      `${environment.apiEndpoint}/public/sign-in`,
      credentials,
      {
        headers,
      }
    );
  }

  hasPermission(code: string): boolean {
    try {
      const token = this.currentUserValue;
      if (token) {
        const permission = token.permissions;

        return permission!.includes(code) || permission!.includes('AccessAll');
      }
    } catch (err) {
      console.warn('Cannot parse token from storage');
      return false;
    }

    return false;
  }

  // Phase 3: read the real "none"/"read"/"write" level packed as "{Module}:{W|R}" in
  // token.permissions (a granted-but-absent-level row never reaches this string at all, so no
  // match here means 'none'). AccessAll — the legacy full-access permission some roles carry —
  // always resolves to 'write', matching hasPermission()'s existing AccessAll bypass.
  getAccessLevel(code: string): 'none' | 'read' | 'write' {
    try {
      const token = this.currentUserValue;
      if (token?.permissions) {
        if (token.permissions.includes('AccessAll')) {
          return 'write';
        }

        const entry = token.permissions.split(',').find((p) => p.split(':')[0] === code);
        if (!entry) {
          return 'none';
        }

        return entry.split(':')[1] === 'W' ? 'write' : 'read';
      }
    } catch (err) {
      console.warn('Cannot parse token from storage');
    }

    return 'none';
  }

  hasWriteAccess(code: string): boolean {
    return this.getAccessLevel(code) === 'write';
  }

  // Modules only, level suffix (":W"/":R") stripped — for exact array-membership checks against
  // a plain code list, as opposed to hasPermission()'s raw-string substring check.
  private getPermissionModules(): string[] {
    const token = this.currentUserValue;
    return token?.permissions?.split(',').map((p) => p.split(':')[0]) ?? [];
  }

  hasActiveMenu(codes: string[]): boolean {
    try {
      const token = this.currentUserValue;
      if (token) {
        // const permission: any = token.permissions?.split(',');
        const permission = this.getPermissionModules();
        return (
          codes.some((code) => permission.includes(code)) ||
          permission!.includes('AccessAll')
        );
      }
    } catch (err) {
      console.warn('Cannot parse token from storage');
      return false;
    }

    return false;
  }

  hasActiveMenuExcludeAccessAll(codes: string[]): boolean {
    try {
      const token = this.currentUserValue;
      if (token) {
        // const permission: any = token.permissions?.split(',');
        const permission = this.getPermissionModules();

        if (permission.includes('AccessAll')) return false;

        return codes.some((code) => permission.includes(code));
      }
    } catch (err) {
      console.warn('Cannot parse token from storage');
      return false;
    }

    return false;
  }

  getUserPermission(): any {
    try {
      const token = this.currentUserValue;
      if (token) {
        const permissions = token.permissions;
        return permissions;
      }
    } catch (err) {
      console.warn('Cannot parse token from storage');
      return [];
    }
  }

  hasPermissions(codes: string[]): boolean {
    try {
      const token = this.currentUserValue;
      if (token) {
        // const permission: any = token.permissions?.split(',');
        const permission = this.getPermissionModules();
        return (
          codes.some((code) => permission.includes(code)) ||
          permission!.includes('AccessAll')
        );
      }
    } catch (err) {
      console.warn('Cannot parse token from storage');
      return false;
    }

    return false;
  }

  hasPermissionRoles(roleChecks: string[]) {
    try {
      const token = this.currentUserValue;
      if (token && token.roles) {
        const roles = token.roles?.split(',');
        if (!roles || roles.length == 0) {
          return false;
        } else {
          const result = roleChecks.filter((element) =>
            roles.includes(element)
          );
          return result.length > 0;
        }
      }
    } catch (err) {
      console.warn('Cannot parse token from storage');
      return false;
    }

    return false;
  }

  hasRole(code: string): boolean {
    try {
      const token = this.currentUserValue;
      if (token) {
        const roles = token.roles;
        const rolesArray = roles?.split(',');
        if(!rolesArray ||rolesArray.length == 0)
          return false;

        return rolesArray.includes(code);
      }
    } catch (err) {
      console.warn('Cannot parse token from storage');
      return false;
    }

    return false;
  }


  hasAnyRoles(codes: string[]): boolean {
    try {
      const token = this.currentUserValue;
      if (token) {
        const roles = token.roles;
        const rolesArray = roles?.split(',');
        if(!rolesArray ||rolesArray.length == 0)
          return false;

        return codes.some((code) => rolesArray.includes(code));
      }
    } catch (err) {
      console.warn('Cannot parse token from storage');
      return false;
    }

    return false;
  }


  hasStatus(code: string): boolean {
    try {
      const token = this.currentUserValue;
      if (token) {
        return token.apply_status === code;
      }
    } catch (err) {
      console.warn('Cannot parse token from storage');
      return false;
    }

    return false;
  }

  public get isKycApprovedUser(): boolean {
    return this.hasRole(Role.User) && this.hasStatus(Status.Approved);
  }

  public get isAdminUser(): boolean {
    return this.hasRole(Role.SuperAdmin) || this.hasRole(Role.SystemAdmin);
  }

  // Single source of truth for "Client-family" roles — the tenant owner (Client) plus every
  // sub-role a tenant can create for its own staff. Add a new tenant-created role here only;
  // isClientUser and every usesPermissionSystem-style check pick it up automatically.
  private static readonly CLIENT_FAMILY_ROLES: Role[] = [
    Role.Client,
    Role.ClientStaff,
    Role.ClientMirror,
    Role.ClientHR,
    Role.ClientAdminViewOnly,
    Role.ClientTimesheet,
    Role.ClientReport,
  ];

  public get isClientUser(): boolean {
    return this.hasPermissionRoles(AuthService.CLIENT_FAMILY_ROLES);
  }

  public get isUser(): boolean {
    return this.hasRole(Role.User);
  }

  // Priority order for post-login/reload redirect — mirrors the real Client sidebar order
  // (rfr-nav-admin.component.ts -> getClientMenuItems()). Walked top-to-bottom; the first
  // permission the user actually has wins, since a user can be granted more than one at once
  // and the redirect can only pick a single destination.
  private readonly menuPriorityRoutes: { permission: string; route: string }[] = [
    Permission.Dashboard,
    Permission.RosterCalendar,
    Permission.Chat,
    Permission.VoiceComms,
    Permission.UsersAdmin,
    Permission.UsersStaff,
    Permission.UsersWorkgroups,
    Permission.UsersRolePermissions,
    Permission.SetupAssignments,
    Permission.SetupSites,
    Permission.SetupShifts,
    Permission.SetupRosters,
    Permission.SetupTags,
    Permission.SetupWorkPolicies,
    Permission.AttendanceManagementRosterBased,
    Permission.AttendanceManagementStaffBased,
    Permission.AttendanceResolution,
  ].map(permission => ({ permission, route: PERMISSION_ROUTES[permission] }));

  // Used for the Client-family catch-all (tenant owner + every tenant-created sub-role) in
  // both login.page.ts (right after login) and RoleRedirectGuard (page reload / cold start).
  // Every role a tenant can create, plus the tenant owner and platform admin, is driven by the
  // PermissionRole table — if none of the menu-priority permissions match, that user has been
  // granted literally nothing yet, so send them to the standalone Welcome page instead of a
  // blank /main/admin shell.
  getDefaultLandingRoute(): string {
    const match = this.menuPriorityRoutes.find(entry => this.hasPermission(entry.permission));
    if (match) {
      return match.route;
    }

    const usesPermissionSystem = this.isClientUser || this.isAdminUser;
    return usesPermissionSystem ? '/welcome' : '/main/admin';
  }

  async getRefreshToken(): Promise<string | null> {
    const currentUserValue = await this.getCurrentUserValue();
    return currentUserValue.refresh_token!;
  }

  refreshToken(): Observable<string> {
    return from(this.getRefreshToken()).pipe(
      switchMap((token) =>
        // this.http.post(`${this.apiUrl}/auth/refresh`, { refreshToken: token }).pipe(
        //   tap(async (res: any) => {
        //     // await this.setTokens(res.accessToken, res.refreshToken);
        //     const model = this.currentUserValue;
        //     model.access_token = res.accessToken;
        //     model.refresh_token = res.refreshToken;
        //     this.set(model);
        //   }),
        //   map((res: any) => res.accessToken),
        //   catchError(err => {
        //     this.logout(); // refresh failed -> logout
        //     throw err;
        //   })
        // )
        {
          let os = 'browser';
          if (this.platform.is('android')) {
            os = 'android';
          }
          if (this.platform.is('ios')) {
            os = 'ios';
          }
          const requestModel = {
            refreshToken: token,
            platform: os,
            token: '',
          };

          return this.apiService
            .postWithoutLoading('/user/auth/refresh', requestModel)
            .pipe(
              tap(async (res: any) => {
                // console.log(res);
                const model = { ...this.currentUserValue };
                model.access_token = res.accessToken;
                model.refresh_token = res.refreshToken;
                this.set(model);

                // Reconect with new token
                await this.signalRService.stopConnection();
                await this.signalRService.startConnection(res.accessToken!);
                await this.signalRService.addListener();
              }),
              map((res: any) => res.accessToken),
              catchError((err) => {
                this.logout(); // refresh failed -> logout
                throw err;
              })
            );
        }
      )
    );
  }

  // public get isKoperasiUser() :boolean {
  //   return this.hasRole(Role.Koperasi);
  // }

  forgotPassword(model: any) {
    return this.apiService.post('/authentication/forgot-password', model);
  }

  resetPassword(model: any) {
    return this.apiService.post('/authentication/reset-password', model);
  }

  changePassword(model: any) {
    return this.apiService.post('/authentication/change-password', model);
  }

  // ── IssueLog API Token Management ──────────────────────────────────────────

  private readonly ISSUE_LOG_TOKEN_KEY = 'issueLog_token';
  private readonly ISSUE_LOG_CREDENTIALS_KEY = 'issueLog_credentials';

  private issueLogTokenSubject = new BehaviorSubject<string | null>(
    localStorage.getItem('issueLog_token')
  );
  public issueLogToken$ = this.issueLogTokenSubject.asObservable();

  private issueLogCredentialsSubject = new BehaviorSubject<{ appId: string; appName: string } | null>(
    (() => {
      const stored = localStorage.getItem('issueLog_credentials');
      return stored ? JSON.parse(stored) : null;
    })()
  );
  public issueLogCredentials$ = this.issueLogCredentialsSubject.asObservable();

  private initializeIssueLogToken(): void {
    const storedToken = localStorage.getItem(this.ISSUE_LOG_TOKEN_KEY);
    const raw = localStorage.getItem(this.ISSUE_LOG_CREDENTIALS_KEY);
    const storedCredentials = raw ? JSON.parse(raw) : null;
    if (storedToken) this.issueLogTokenSubject.next(storedToken);
    if (storedCredentials) this.issueLogCredentialsSubject.next(storedCredentials);
  }

  generateIssueLogToken(apiKey?: string, appId?: string): Observable<{ token: string; expiresIn: string; appId: string }> {
    const url = `${environment.issueApiEndpoint}/api/auth/token`;
    const finalApiKey = apiKey || APP_CONFIG.apiKey;
    const finalAppId = appId || APP_CONFIG.appId;

    return this.http.post<{ token: string; expiresIn: string; appId: string }>(url, { apiKey: finalApiKey, appId: finalAppId }).pipe(
      tap(response => {
        localStorage.setItem(this.ISSUE_LOG_TOKEN_KEY, response.token);
        const credentials = { appId: response.appId, appName: APP_CONFIG.appName };
        localStorage.setItem(this.ISSUE_LOG_CREDENTIALS_KEY, JSON.stringify(credentials));
        this.issueLogTokenSubject.next(response.token);
        this.issueLogCredentialsSubject.next(credentials);
      }),
      catchError(error => {
        console.error('Error generating IssueLog token:', error);
        return throwError(() => error);
      })
    );
  }

  getIssueLogToken(): string | null {
    return this.issueLogTokenSubject.value;
  }

  getIssueLogCredentials(): { appId: string; appName: string } | null {
    return this.issueLogCredentialsSubject.value;
  }

  isIssueLogAuthenticated(): boolean {
    return !!this.getIssueLogToken() && !!this.getIssueLogCredentials();
  }

  logoutIssueLog(): void {
    localStorage.removeItem(this.ISSUE_LOG_TOKEN_KEY);
    localStorage.removeItem(this.ISSUE_LOG_CREDENTIALS_KEY);
    this.issueLogTokenSubject.next(null);
    this.issueLogCredentialsSubject.next(null);
  }
}
